import { NextLink } from '@/features/shared/elements/Link'
import { GridLayoutContainer } from '@/features/shared/styles/layout'
import { MenuIcon } from '@shipt/design-system-icons'
import { screenSizes } from '@shipt/design-system-themes'
import styled from 'styled-components'
import { HeaderLogoLink } from '@/features/shared/components/Headers/HeaderLogoLink'
import { Body } from '@shipt/design-system-typography'
import { type SpecializedHeaderData } from '@/features/cms/components/CMS/types'
import {
  CareersLinks,
  DEIBLinks,
  EDSILinks,
} from '@/features/shared/components/Headers/CMSHeader/SpecializedHeaderConstants'
import { useHandleDialog } from '@/features/shared/state/Dialog/useHandleDialog'
import { DynamicFullSizeMobileMenuModal } from '@/features/shared/components/Modal/FullSizeMobileMenuModal/DynamicFullSizeMobileMenuModal'
import { Row } from '@shipt/design-system-layouts'
import { IconButton } from '@shipt/design-system-buttons'

export const PARTNER_LINKS = {
  deib: DEIBLinks,
  careers: CareersLinks,
  edsi: EDSILinks,
}

/**
 * This component corresponds to the `specialized_header` content type in the CMS.
 *
 * - **Staging** - https://cms-staging.shipt.com/content-type/pool/specialized_header
 * - **Production** - https://cms.shipt.com/content-type/pool/specialized_header
 */
export const SpecializedHeader = ({ data: { partner } }: SpecializedHeaderData) => {
  const { openDialog, closeDialog } = useHandleDialog()

  const links = partner ? PARTNER_LINKS[partner] : []

  return (
    <GridLayoutContainer contentMaxWidth>
      <HeaderLayoutContainer align="center" spacing="sm">
        <MenuButton
          icon={MenuIcon}
          size="md"
          variant="ghost"
          onClick={() =>
            openDialog(DynamicFullSizeMobileMenuModal, {
              renderMenu: () => <MobileMenu links={links} closeMenu={closeDialog} />,
            })
          }
          aria-label="Main menu"
        />
        <HeaderLogoLink />
        <NavLinkContainer>
          {links.map((link) => (
            <NextLink key={link.href} href={link.href} target="_blank" rel="noopener noreferrer">
              <Body strong>{link.label}</Body>
            </NextLink>
          ))}
        </NavLinkContainer>
      </HeaderLayoutContainer>
    </GridLayoutContainer>
  )
}

const MobileMenu = ({
  links,
  closeMenu,
}: {
  links: { href: string; label: string }[]
  closeMenu: () => void
}) => (
  <MobileMenuWrapper>
    <MobileNavLinkContainer>
      {links.map((link) => (
        <NextLink
          key={link.href}
          href={link.href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={closeMenu}
        >
          <Body strong>{link.label}</Body>
        </NextLink>
      ))}
    </MobileNavLinkContainer>
  </MobileMenuWrapper>
)

const HeaderLayoutContainer = styled(Row)`
  height: 4.5rem;
`
const MenuButton = styled(IconButton)`
  @media ${screenSizes.smDesktop} {
    display: none;
  }
`
const NavLinkContainer = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 1.5rem;
  margin-left: 24px;
  overflow-x: auto;

  @media ${screenSizes.max_mobile} {
    display: none;
  }

  @media ${screenSizes.only_tablet} {
    display: none;
  }
`
const MobileMenuWrapper = styled.div`
  @media ${screenSizes.smDesktop} {
    display: none;
  }
`

const MobileNavLinkContainer = styled(NavLinkContainer)`
  flex-direction: column;
  gap: 2rem;
  display: flex;
  max-width: fit-content;
`
